import { AchievementName, ACHIEVEMENTS_CONFIGS_MAP } from '@helios/achievement';
import { Button, Link, useNavigation, useTranslation } from '@helios/frontend-core';

import { PalmettoProgressBar, PalmettoTag } from '@palmetto/dsm-components';
import { NavigateOptions, ToOptions } from '@tanstack/react-router';
import { ApiUser } from '../../../../api/user/types';
import { OpenTakeover, useTakeoverContext } from '../../../_app/TakeoverProvider/TakeoverContext';

import { ACHIEVEMENT_TO_TARGET_MAP } from '../../badgeConfig';
import { shouldShowProgressAndTotal } from '../../BadgeGoalLabel/BadgeGoalLabelConfig';
import { BadgeIcon } from '../../BadgeIcon/BadgeIcon';
import { badgeDetailDescriptionMap } from '../BadgeDetailTakeoverConfig';
import { DetailGoalLabel } from './BadgeDetailGoalLabel';

export interface BadgeDetailViewProps {
  achievementName: AchievementName;
  achieved?: boolean;
  numberDone?: number;
  total?: number;
  dateEarned?: string;
  trackingId: string;
  onLinkClick?: () => void;
  onActionClick?: (context: { user: any; propertyId?: string; openTakeover: OpenTakeover }) => void;
  user?: ApiUser;
  propertyId?: string;
  openTakeover: OpenTakeover;
}

export const BadgeDetailView: React.FC<BadgeDetailViewProps> = ({
  achievementName,
  achieved = false,
  numberDone = 0,
  total,
  dateEarned,
  trackingId,
  onLinkClick,
  onActionClick,
  user,
  propertyId,
  openTakeover,
}) => {
  const { t } = useTranslation();
  const { closeTakeover } = useTakeoverContext();
  const { openURL } = useNavigation();

  const achievementConfig = ACHIEVEMENTS_CONFIGS_MAP[achievementName];
  const pointsReward = achievementConfig?.pointsReward;
  const couponValue = achievementConfig?.couponReward;
  const isCoupon = Boolean(couponValue);
  const reward = isCoupon
    ? t('common.couponDisplay', { num: couponValue })
    : t('common.pointsDisplay', { points: pointsReward });
  const badgeTotal = total ?? ACHIEVEMENT_TO_TARGET_MAP[achievementName];
  const showProgressBar = shouldShowProgressAndTotal.includes(achievementName);

  const cta = achieved ? achievementConfig?.achievedCta : achievementConfig?.unachievedCta;

  const handleCtaClick = () => {
    if (cta?.destination) {
      closeTakeover();
      openURL(cta.destination as ToOptions & NavigateOptions);
    }
  };

  return (
    <div className="flex flex-col items-center h-full gap-12 pt-32 m-32">
      <BadgeIcon achievementName={achievementName} achieved={achieved} className="w-[200px] h-[182px]" />
      <DetailGoalLabel
        className="!text-text-primary"
        achieved={achieved}
        numberDone={numberDone}
        achievementName={achievementName}
      />
      {!achieved && showProgressBar && (
        <div className="flex justify-center items-center">
          <PalmettoProgressBar className="mt-8 mb-8 !w-[200px]" position={numberDone} max={badgeTotal} />
        </div>
      )}
      {achieved && <PalmettoTag icon={'check'} color={'volt'} label={reward} canvas="inverted" />}
      {!achieved && (
        <PalmettoTag color={'grey'} icon={isCoupon ? 'discountScissorsCut' : 'palmTree'} label={reward} />
      )}

      {dateEarned && <span className="body-300 text-text-primary">{t('common.onDate', { date: dateEarned })}</span>}
      <div className="text-text-primary body-500 text-center">
        {(() => {
          const descConfig = badgeDetailDescriptionMap[achievementName];
          const earnThisBadgeText = t('badge.detail.earnThisBadge');
          const goalValue = ACHIEVEMENT_TO_TARGET_MAP[achievementName];
          const questName = t(`badge.achievement.${achievementName}`);

          const interpolationArgs = {
            goal: goalValue,
            questName,
            ...descConfig.args,
          };

          if (descConfig.url) {
            return (
              <span>
                {earnThisBadgeText}{' '}
                <Link
                  trackingId={trackingId}
                  href={descConfig.url}
                  className="text-text-primary"
                  onClick={onLinkClick}
                >
                  {t(descConfig.locale, interpolationArgs)}
                </Link>
              </span>
            );
          }
          if (descConfig.onClick && onActionClick) {
            return (
              <span>
                {earnThisBadgeText}{' '}
                <Link
                  className="text-text-primary"
                  trackingId={trackingId}
                  onClick={() => {
                    onActionClick({ user, propertyId, openTakeover });
                  }}
                >
                  {t(descConfig.locale, interpolationArgs)}
                </Link>
              </span>
            );
          }
          return t(descConfig.locale, interpolationArgs);
        })()}
      </div>
      {cta && (
        <Button size="medium" layout="hug" onClick={handleCtaClick} as="a" trackingId={`${trackingId}-cta`}>
          {t(cta.labelLocale)}
        </Button>
      )}
    </div>
  );
};
